// ballisticsMath.js
// Pure, dependency-free exterior-ballistics engine for airgun pellets & slugs.
// Point-mass (3-DOF) numerical integration using the standard G1 drag model.
// All calculations are done in SI units (metres, m/s, kg, seconds, Joules).
// BC is the conventional G1 ballistic coefficient (lb/in^2).
//
// This module intentionally contains NO React so it can be unit-tested in isolation.

export const GRAVITY = 9.80665; // m/s^2

// Drag constant that converts a G1 BC (lb/in^2) into an SI retardation.
// Derived from: a_drag = 0.5 * rho * v^2 * (Cd_bullet * A / m)
// with (Cd*A/m) = (pi/4) * Cd_G1(Mach) / BC, converted from in^2/lb to m^2/kg.
// => a_drag = C_DRAG * rho * v^2 * Cd_G1(Mach) / BC
export const C_DRAG = 0.00055855;

// Standard G1 drag-coefficient table: Mach -> Cd.
// Subsonic region (where airguns operate) is the most important and is well resolved.
const G1_TABLE = [
  [0.00, 0.2629], [0.05, 0.2558], [0.10, 0.2487], [0.15, 0.2413],
  [0.20, 0.2344], [0.25, 0.2278], [0.30, 0.2214], [0.35, 0.2155],
  [0.40, 0.2104], [0.45, 0.2061], [0.50, 0.2032], [0.55, 0.2020],
  [0.60, 0.2034], [0.65, 0.2092], [0.70, 0.2165], [0.725, 0.2230],
  [0.75, 0.2313], [0.775, 0.2417], [0.80, 0.2546], [0.825, 0.2706],
  [0.85, 0.2901], [0.875, 0.3136], [0.90, 0.3415], [0.925, 0.3734],
  [0.95, 0.4084], [0.975, 0.4448], [1.00, 0.4805], [1.025, 0.5136],
  [1.05, 0.5427], [1.075, 0.5677], [1.10, 0.5883], [1.125, 0.6053],
  [1.15, 0.6191], [1.20, 0.6393], [1.25, 0.6518], [1.30, 0.6589],
  [1.40, 0.6621], [1.50, 0.6553], [1.60, 0.6437], [1.80, 0.6154],
  [2.00, 0.5883], [2.20, 0.5636], [2.50, 0.5313], [3.00, 0.4952],
];

// Linear interpolation of the G1 drag coefficient at a given Mach number.
export function dragCd(mach) {
  if (mach <= G1_TABLE[0][0]) return G1_TABLE[0][1];
  const last = G1_TABLE[G1_TABLE.length - 1];
  if (mach >= last[0]) return last[1];
  for (let i = 1; i < G1_TABLE.length; i++) {
    const [m1, c1] = G1_TABLE[i];
    if (mach <= m1) {
      const [m0, c0] = G1_TABLE[i - 1];
      const f = (mach - m0) / (m1 - m0);
      return c0 + f * (c1 - c0);
    }
  }
  return last[1];
}

// Speed of sound (m/s) for dry air at a given temperature (deg C).
export function speedOfSound(tempC) {
  return 331.3 * Math.sqrt(1 + tempC / 273.15);
}

// Air density (kg/m^3) from temperature (deg C) and either a measured surface
// pressure (hPa) or, if none given, altitude (m) via the barometric formula.
export function airDensity(tempC, altitudeM = 0, pressureHpa = null) {
  const tempK = tempC + 273.15;
  let pressure;
  if (pressureHpa != null && pressureHpa > 0) {
    pressure = pressureHpa * 100; // hPa -> Pa
  } else {
    const p0 = 101325; // Pa at sea level
    pressure = p0 * Math.pow(1 - 2.25577e-5 * altitudeM, 5.25588);
  }
  return pressure / (287.05 * tempK);
}

// Kinetic energy in Joules. mass in kg, velocity in m/s.
export function kineticEnergy(massKg, velocityMs) {
  return 0.5 * massKg * velocityMs * velocityMs;
}

// Integrate one trajectory for a given bore angle (rad, relative to line of sight)
// and sample the requested downrange distances (m, ascending).
// Coordinate frame: x downrange, y vertical (up +), z lateral (right +).
// The bullet starts one scope-height below the line of sight (y = -sightHeight).
function integrateSamples(angleRad, p, env, distances) {
  const dt = 0.0004;
  let x = 0;
  let y = -p.sightHeight;
  let z = 0;
  let vx = p.v0 * Math.cos(angleRad);
  let vy = p.v0 * Math.sin(angleRad);
  let vz = 0;
  let t = 0;

  // Wind FROM clock direction (deg): 0=front(head), 90=right, 180=back(tail), 270=left.
  const phi = (p.windDir * Math.PI) / 180;
  const Wx = -p.windSpeed * Math.cos(phi);
  const Wz = -p.windSpeed * Math.sin(phi);

  const gEff = GRAVITY * Math.cos((p.inclination * Math.PI) / 180);

  const results = new Array(distances.length).fill(null);
  let idx = 0;
  const maxD = distances[distances.length - 1];

  let prev = { x, y, z, vx, vy, vz, t };

  const record = (frac, a, b, dist) => ({
    distanceM: dist,
    dropM: a.y + frac * (b.y - a.y),
    driftM: a.z + frac * (b.z - a.z),
    velocityMs: (() => {
      const va = Math.hypot(a.vx, a.vy, a.vz);
      const vb = Math.hypot(b.vx, b.vy, b.vz);
      return va + frac * (vb - va);
    })(),
    timeS: a.t + frac * (b.t - a.t),
  });

  let guard = 0;
  while (idx < distances.length && guard < 200000) {
    guard++;
    const rvx = vx - Wx;
    const rvy = vy;
    const rvz = vz - Wz;
    const vrel = Math.hypot(rvx, rvy, rvz);
    const mach = vrel / env.sos;
    const k = (C_DRAG * env.rho * dragCd(mach)) / p.bc;

    const ax = -k * vrel * rvx;
    const ay = -k * vrel * rvy - gEff;
    const az = -k * vrel * rvz;

    prev = { x, y, z, vx, vy, vz, t };
    vx += ax * dt;
    vy += ay * dt;
    vz += az * dt;
    x += vx * dt;
    y += vy * dt;
    z += vz * dt;
    t += dt;

    // Record any sample distances crossed in this step.
    while (idx < distances.length && x >= distances[idx]) {
      const dist = distances[idx];
      const frac = (dist - prev.x) / (x - prev.x || 1e-9);
      results[idx] = record(frac, prev, { x, y, z, vx, vy, vz, t }, dist);
      idx++;
    }

    // Abort if the projectile falls far below the sight line before reaching range.
    if (y < -50 || x > maxD + 1) break;
  }

  // Fill any unreached samples with the last known state (projectile fell short).
  for (let i = 0; i < results.length; i++) {
    if (!results[i]) {
      results[i] = {
        distanceM: distances[i],
        dropM: y,
        driftM: z,
        velocityMs: Math.hypot(vx, vy, vz),
        timeS: t,
      };
    }
  }
  return results;
}

// Solve for the bore angle (rad) that puts the trajectory back on the line of
// sight at the zero distance. Uses bisection (drop is monotonic in angle).
function solveZeroAngle(p, env) {
  const target = p.zeroDistance;
  const dropAt = (angle) => integrateSamples(angle, p, env, [target])[0].dropM;
  let lo = 0;
  let hi = 0.2; // ~11.5 deg, plenty for low-velocity airguns at long range
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (dropAt(mid) < 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

const MRAD_TO_MOA = 3.43774677;

// Main entry point. Accepts SI inputs and returns samples + key metrics.
// input: {
//   v0, bc, massKg, sightHeight, zeroDistance, targetDistance,
//   windSpeed, windDir, tempC, altitude, inclination
// }
export function computeBallistics(input) {
  const p = {
    v0: input.v0,
    bc: Math.max(input.bc, 0.001),
    sightHeight: input.sightHeight,
    zeroDistance: Math.max(input.zeroDistance, 1),
    targetDistance: Math.max(input.targetDistance, 1),
    windSpeed: input.windSpeed || 0,
    windDir: input.windDir || 0,
    inclination: input.inclination || 0,
  };
  const env = {
    rho: airDensity(input.tempC, input.altitude, input.pressureHpa),
    sos: speedOfSound(input.tempC),
  };

  const angleRad = solveZeroAngle(p, env);

  // Build the list of sample distances for chart + table.
  const maxDistance = Math.max(
    50,
    Math.ceil((Math.max(p.targetDistance, p.zeroDistance) * 1.3) / 5) * 5
  );
  const step = maxDistance <= 70 ? 5 : 10;
  const distSet = new Set([0, p.zeroDistance, p.targetDistance]);
  for (let d = step; d <= maxDistance; d += step) distSet.add(d);
  const distances = Array.from(distSet).sort((a, b) => a - b);

  const raw = integrateSamples(angleRad, p, env, distances);

  const samples = raw.map((s) => {
    const dist = s.distanceM;
    const dropMrad = dist > 0 ? (s.dropM / dist) * 1000 : 0;
    const driftMrad = dist > 0 ? (s.driftM / dist) * 1000 : 0;
    return {
      distanceM: dist,
      dropM: s.dropM,
      dropMrad,
      dropMoa: dropMrad * MRAD_TO_MOA,
      driftM: s.driftM,
      driftMrad,
      driftMoa: driftMrad * MRAD_TO_MOA,
      velocityMs: s.velocityMs,
      energyJ: kineticEnergy(input.massKg, s.velocityMs),
      timeS: s.timeS,
    };
  });

  const target = samples.find((s) => Math.abs(s.distanceM - p.targetDistance) < 1e-6) || samples[samples.length - 1];

  const metrics = {
    angleRad,
    airDensity: env.rho,
    speedOfSound: env.sos,
    e0J: kineticEnergy(input.massKg, input.v0),
    maxDistance,
    step,
    targetDistanceM: p.targetDistance,
    targetVelocityMs: target.velocityMs,
    targetEnergyJ: target.energyJ,
    targetDropM: target.dropM,
    targetDropMrad: target.dropMrad,
    targetDropMoa: target.dropMoa,
    windDriftM: target.driftM,
    windDriftMrad: target.driftMrad,
    windDriftMoa: target.driftMoa,
    flightTimeS: target.timeS,
  };

  return { samples, metrics };
}

// Maximum Point-Blank Range (MPBR).
// Given a vital "hit window" diameter (m), find the launch angle whose trajectory
// peaks exactly at the top of the window (+window/2 above the line of sight),
// then report the near/far zeros, the max ordinate, and MPBR = the distance where
// the descending path crosses the bottom of the window (-window/2).
export function computeMPBR(input, vitalWindowM) {
  const vr = Math.max(vitalWindowM, 0.001) / 2;
  const p = {
    v0: input.v0,
    bc: Math.max(input.bc, 0.001),
    sightHeight: input.sightHeight,
    windSpeed: 0,
    windDir: 0,
    inclination: 0,
  };
  const env = {
    rho: airDensity(input.tempC, input.altitude, input.pressureHpa),
    sos: speedOfSound(input.tempC),
  };
  const maxScan = 300;
  const distances = [];
  for (let d = 0; d <= maxScan; d += 1) distances.push(d);

  const pathFor = (angle) => integrateSamples(angle, p, env, distances);
  const peakOf = (arr) => {
    let m = -Infinity;
    for (const s of arr) if (s.dropM > m) m = s.dropM;
    return m;
  };

  // Bisection on angle: max ordinate increases monotonically with launch angle.
  let lo = 0;
  let hi = 0.3;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (peakOf(pathFor(mid)) < vr) lo = mid;
    else hi = mid;
  }
  const angle = (lo + hi) / 2;
  const path = pathFor(angle);

  const interpX = (a, b, y) =>
    a.distanceM + ((y - a.dropM) / (b.dropM - a.dropM)) * (b.distanceM - a.distanceM);

  let nearZero = null;
  let farZero = null;
  let mpbr = null;
  let maxOrd = -Infinity;
  let maxOrdDist = 0;

  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    if (b.dropM > maxOrd) {
      maxOrd = b.dropM;
      maxOrdDist = b.distanceM;
    }
    if (nearZero === null && a.dropM < 0 && b.dropM >= 0) nearZero = interpX(a, b, 0);
    if (nearZero !== null && farZero === null && a.dropM >= 0 && b.dropM < 0) farZero = interpX(a, b, 0);
    if (mpbr === null && a.dropM >= -vr && b.dropM < -vr && b.distanceM > maxOrdDist) {
      mpbr = interpX(a, b, -vr);
    }
  }
  if (mpbr === null) mpbr = maxScan;

  return {
    angleRad: angle,
    vitalWindowM: vr * 2,
    nearZeroM: nearZero,
    farZeroM: farZero,
    mpbrM: mpbr,
    maxOrdinateM: maxOrd,
    maxOrdinateDistM: maxOrdDist,
  };
}

export const CONVERT = {
  MRAD_TO_MOA,
  gToGr: (g) => g * 15.4323584,
  grToG: (gr) => gr / 15.4323584,
  msToFps: (ms) => ms * 3.28083989,
  fpsToMs: (fps) => fps / 3.28083989,
  mToYd: (m) => m * 1.0936133,
  ydToM: (yd) => yd / 1.0936133,
  mToIn: (m) => m * 39.3700787,
  inToM: (inch) => inch / 39.3700787,
  jToFtlb: (j) => j * 0.737562149,
  msToMph: (ms) => ms * 2.23693629,
  mphToMs: (mph) => mph / 2.23693629,
  cToF: (c) => (c * 9) / 5 + 32,
  fToC: (f) => ((f - 32) * 5) / 9,
};
